import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';

const HeroSection = () => {
  const [currentTitle, setCurrentTitle] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const { isDark } = useTheme();

  const titles = [
    'Software Engineer',
    'Full-Stack Developer',
    'Research & Development',
    'Tech Innovator'
  ];

  useEffect(() => {
    let timeout;
    const currentTitleText = titles[currentTitle];
    
    if (isTyping) {
      if (displayText.length < currentTitleText.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentTitleText.slice(0, displayText.length + 1));
        }, 100);
      } else {
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, 2000);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 50);
      } else {
        setCurrentTitle((prev) => (prev + 1) % titles.length);
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isTyping, currentTitle, titles]);

  const particleVariants = {
    animate: {
      y: [0, -20, 0],
      opacity: [0.3, 0.8, 0.3],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Function to handle CV download
  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/assets/Lahiru Senavirathna.pdf';
    link.download = 'Lahiru Senavirathna.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-surface to-muted overflow-hidden">
      {/* Particle Animation Background */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            variants={particleVariants}
            animate="animate"
            transition={{ delay: i * 0.2 }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-12 sm:pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-6rem)]">
          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Greeting */}
            <div className="space-y-2">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg text-text-secondary font-medium"
              >
                Hello, I'm
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary"
              >
                Lahiru Senavirathna
              </motion.h1>
            </div>

            {/* Typewriter Effect */}
            <div className="h-16 flex items-center">
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold gradient-text"
              >
                {displayText}
                <span className="animate-pulse">|</span>
              </motion.h2>
            </div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="text-lg text-text-secondary leading-relaxed max-w-xl"
            >
              Enthusiastic tech lover with over 5 years of experience in software engineering, specializing in 
              full-stack development and research & development. Committed to developing innovative solutions that 
              simplify life. Skilled in rapidly learning new technologies and adapting seamlessly to diverse work 
              environments. Passionate about open-source contributions, with a focus on creating clean, well-organized 
              codebases that deliver exceptional user experiences.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/projects-innovation-laboratory">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Code"
                  iconPosition="left"
                  className="w-full sm:w-auto hover-lift"
                >
                  View My Work
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="w-full sm:w-auto hover-lift"
                onClick={handleDownloadCV}
              >
                Download CV
              </Button>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="flex flex-wrap gap-6 pt-4"
            >
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={20} className="text-primary" />
                <span className="text-text-secondary">5+ Years Experience</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={20} className="text-primary" />
                <span className="text-text-secondary">Homagama, Sri Lanka</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
                <span className="text-success font-medium">Available for hire</span>
              </div>
            </motion.div>

            {/* Founder of CodeShow LapZ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col items-start space-y-1 mt-2"
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg text-text-secondary">Founder of</span>
                <span className="text-2xl font-bold gradient-text">CodeShow LapZ</span>
              </div>
              <p className="text-sm text-text-muted italic">
                "Where Code Meets Innovation" - Premium Software Solutions & Development Services
              </p>
            </motion.div>
          </motion.div>

          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Background Decoration */}
              <div className="absolute -inset-6 bg-gradient-to-br from-primary/20 to-brand-purple/20 rounded-2xl blur-2xl"></div>
              
              {/* Main Image Container - Increased size */}
              <div className={`relative ${isDark ? 'bg-slate-800/10' : 'bg-white/10'} backdrop-blur-sm rounded-2xl p-3 border ${isDark ? 'border-gray-700/20' : 'border-white/20'}`}>
                <div className="w-[22rem] h-[22rem] lg:w-[28rem] lg:h-[28rem] rounded-xl overflow-hidden">
                  <Image
                    src="/assets/home.png"
                    alt="Lahiru Senavirathna - Full-Stack Engineer"
                    className="w-full h-full object-cover hover-scale transition-premium"
                  />
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`absolute -top-4 -right-4 ${isDark ? 'bg-slate-800/90' : 'bg-white/90'} backdrop-blur-sm rounded-lg p-3 shadow-premium border ${isDark ? 'border-gray-700/20' : 'border-white/20'}`}
              >
                <Icon name="Code2" size={24} className="text-primary" />
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2.5, repeat: Infinity }}
                className={`absolute -bottom-4 -left-4 ${isDark ? 'bg-slate-800/90' : 'bg-white/90'} backdrop-blur-sm rounded-lg p-3 shadow-premium border ${isDark ? 'border-gray-700/20' : 'border-white/20'}`}
              >
                <Icon name="Zap" size={24} className="text-brand-purple" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center space-y-2"
        >
          <span className="text-sm text-text-muted">Scroll to explore</span>
          <Icon name="ChevronDown" size={20} className="text-text-muted" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;